/**
 * 视频组节点：按顺序排列的多段视频，可导出为一段视频（后端 ffmpeg 拼接）。
 *
 * - 数组顺序即拼接顺序，格子上提供前移 / 后移 / 移除
 * - 导出是后台任务：进度写回节点 data，刷新后组件重新挂载即可续接轮询
 * - 完成后在组右侧新建一个视频节点（组保留，可重复导出）
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Film,
  Loader2,
  Scissors,
  Trash2,
  X,
} from 'lucide-react';
import type { CanvasNode, VideoGroupNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeToolbarButton } from './NodeToolbar';
import { boardActions } from '../state/boardStore';
import { makeNode } from '../utils';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { toPersistableMediaUrl } from '../utils/persistableMediaUrl';
import { canvasAPI } from '../services/canvasAPI';
import {
  moveVideoInGroup,
  removeVideoFromGroup,
  removeVideoGroupSlot,
  extractVideoFromGroup,
} from '../generators/videoGroupOps';
import { VideoClipEditor } from './VideoClipEditor';
import { VIDEO_GROUP_MAX_CLIPS, VIDEO_GROUP_MAX_TOTAL_SECONDS } from '../constants';
import toast from '../../../components/Toast';

interface VideoGroupNodeProps {
  node: CanvasNode<VideoGroupNodeData>;
  selected: boolean;
  highlighted: boolean;
}

function formatDuration(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return '--';
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function stageLabel(stage?: string | null): string {
  switch (stage) {
    case 'downloading':
      return '下载分段';
    case 'transcoding':
      return '统一转码';
    case 'concat':
      return '拼接';
    case 'uploading':
      return '上传结果';
    default:
      return '合成中';
  }
}

function SlotButton({
  children,
  onClick,
  disabled,
  title,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="grid h-5 w-5 place-items-center rounded bg-black/60 text-white transition hover:bg-black/85 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

export function VideoGroupNode({ node, selected, highlighted }: VideoGroupNodeProps) {
  const data = node.data as VideoGroupNodeData;
  const urls = data.videoUrls ?? [];
  const covers = data.coverUrls ?? [];
  const slots = data.generatingSlots ?? [];
  const expanded = data.expanded ?? true;
  const exporting = data.concatStatus === 'pending' || data.concatStatus === 'running';
  // 生成中的槽位只展示进度、不参与拼接；未终态时禁止导出，避免导出半成品
  const pendingSlotCount = slots.filter(
    (s) => s.status !== 'completed' && s.status !== 'failed'
  ).length;
  const totalSec = (data.durations ?? []).reduce<number>(
    (sum, d) => sum + (typeof d === 'number' ? d : 0),
    0
  );

  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [editingClip, setEditingClip] = useState<number | null>(null);
  const nodeRef = useRef(node);
  nodeRef.current = node;
  const handledJobRef = useRef<string | null>(null);

  // 合成任务轮询：完成后在组右侧新建视频节点（组保留，可重复导出）；
  // 终态时把 concatJobId 置空，effect 随之卸载并清理定时器
  useEffect(() => {
    const jobId = data.concatJobId;
    if (!jobId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const job = await canvasAPI.getVideoConcatJob(jobId);
        if (cancelled) return;

        if (job.status === 'completed') {
          if (handledJobRef.current === jobId) return;
          handledJobRef.current = jobId;
          const current = nodeRef.current;
          const finalUrl = job.url ? toPersistableMediaUrl(job.url) ?? job.url : null;
          if (finalUrl) {
            boardActions.addNode(
              makeNode('video', { x: current.x + current.width + 60, y: current.y }, {
                jobId: null,
                videoUrl: finalUrl,
                coverUrl: null,
                status: 'completed',
                videoDurationSec: job.durationSec ?? null,
                modelLabel: '视频组导出',
              })
            );
          }
          boardActions.updateNodeData(current.id, {
            concatJobId: null,
            concatStatus: 'completed',
            concatProgress: 100,
            concatStage: null,
            concatError: null,
          });
          toast.success('视频组已导出为一段视频');
          return;
        }

        if (job.status === 'failed') {
          boardActions.updateNodeData(nodeRef.current.id, {
            concatJobId: null,
            concatStatus: 'failed',
            concatProgress: 0,
            concatStage: null,
            concatError: job.errorMessage || '视频合成失败',
          });
          toast.error(job.errorMessage || '视频合成失败');
          return;
        }

        boardActions.updateNodeDataSilent(nodeRef.current.id, {
          concatStatus: job.status,
          concatProgress: job.progress,
          concatStage: job.stage,
        });
      } catch {
        // 网络抖动：下一轮继续轮询
      }
    };

    void tick();
    const timer = setInterval(() => void tick(), 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [data.concatJobId]);

  const handleExport = async () => {
    if (exporting) return;
    if (pendingSlotCount > 0) {
      toast.error('有视频正在生成中，请等待完成后再导出');
      return;
    }
    const validUrls = urls.filter((u): u is string => Boolean(u));
    if (validUrls.length === 0) {
      toast.error('视频组为空，无法导出');
      return;
    }
    if (validUrls.length > VIDEO_GROUP_MAX_CLIPS) {
      toast.error(`最多支持 ${VIDEO_GROUP_MAX_CLIPS} 段视频`);
      return;
    }
    if (totalSec > VIDEO_GROUP_MAX_TOTAL_SECONDS) {
      toast.error(`视频总时长超过 ${Math.round(VIDEO_GROUP_MAX_TOTAL_SECONDS / 60)} 分钟上限`);
      return;
    }

    try {
      const { jobId } = await canvasAPI.submitVideoConcat(validUrls);
      boardActions.updateNodeData(node.id, {
        concatJobId: jobId,
        concatStatus: 'pending',
        concatProgress: 0,
        concatStage: null,
        concatError: null,
      });
      toast.success('已提交视频合成任务');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '提交视频合成任务失败');
    }
  };

  return (
    <NodeWrapper
      node={node}
      selected={selected}
      highlighted={highlighted}
      stretchContent
      toolbar={
        <>
          <NodeToolbarButton
            onClick={() => boardActions.updateNodeData(node.id, { expanded: !expanded })}
            title={expanded ? '收起' : '展开'}
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          </NodeToolbarButton>
          <NodeToolbarButton
            onClick={handleExport}
            disabled={exporting || urls.length === 0 || pendingSlotCount > 0}
            tone="primary"
            title={
              pendingSlotCount > 0
                ? '有视频正在生成中，请等待完成后再导出'
                : `导出为一段视频（${urls.length} 段，共 ${formatDuration(totalSec)}）`
            }
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />}
            {exporting ? `合成中 ${data.concatProgress ?? 0}%` : '导出'}
          </NodeToolbarButton>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {/* 概览：段数 / 总时长 / 生成中 / 顺序说明 */}
        <div className="flex items-center gap-1.5 text-[11px] text-light-muted">
          <Film className="h-3.5 w-3.5" />
          <span>
            {urls.length} 段 · 总时长 {formatDuration(totalSec)}
          </span>
          {pendingSlotCount > 0 && (
            <span className="flex items-center gap-1 text-blue-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              生成中 {pendingSlotCount} 段
            </span>
          )}
          <span className="ml-auto text-[10px] text-slate-400">按顺序拼接</span>
        </div>

        {/* 合成进度 / 失败提示 */}
        {exporting && (
          <div className="flex items-center gap-2 rounded-md bg-blue-50 px-2 py-1.5 text-[11px] text-blue-600">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>
              {stageLabel(data.concatStage)} · {data.concatProgress ?? 0}%
            </span>
          </div>
        )}
        {!exporting && data.concatStatus === 'failed' && data.concatError && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 px-2 py-1.5 text-[11px] text-red-500">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-2">{data.concatError}</span>
          </div>
        )}

        {urls.length === 0 && slots.length === 0 ? (
          <div className="flex h-24 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-light-border text-light-muted">
            <Film className="h-5 w-5" />
            <span className="text-xs">空视频组</span>
            <span className="text-[10px]">把视频节点拖到本节点即可加入</span>
          </div>
        ) : expanded ? (
          <div className="grid min-h-0 flex-1 auto-rows-fr grid-cols-2 gap-2 overflow-y-auto pr-0.5">
            {urls.map((url, i) => (
              <div
                key={`${url}-${i}`}
                className="group/item relative min-h-[4.5rem] overflow-hidden rounded-lg border border-light-border bg-black"
              >
                <button type="button" onClick={() => setPlayingIndex(i)} className="block h-full w-full">
                  <video
                    src={resolveBrowserMediaUrl(url)}
                    poster={covers[i] ? resolveBrowserMediaUrl(covers[i] as string) : undefined}
                    className="h-full w-full object-cover"
                    muted
                    preload="metadata"
                    playsInline
                  />
                </button>
                <span className="pointer-events-none absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] leading-4 text-white">
                  {i + 1}
                </span>
                <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition group-hover/item:opacity-100">
                  <SlotButton onClick={() => setEditingClip(i)} title="剪辑">
                    <Scissors className="h-3 w-3" />
                  </SlotButton>
                  <SlotButton onClick={() => moveVideoInGroup(node.id, i, -1)} disabled={i === 0} title="前移">
                    <ArrowLeft className="h-3 w-3" />
                  </SlotButton>
                  <SlotButton
                    onClick={() => moveVideoInGroup(node.id, i, 1)}
                    disabled={i === urls.length - 1}
                    title="后移"
                  >
                    <ArrowRight className="h-3 w-3" />
                  </SlotButton>
                  <SlotButton onClick={() => extractVideoFromGroup(node.id, i)} title="取出为独立视频节点">
                    <ArrowUpRight className="h-3 w-3" />
                  </SlotButton>
                  <SlotButton onClick={() => removeVideoFromGroup(node.id, i)} title="从视频组移除">
                    <Trash2 className="h-3 w-3" />
                  </SlotButton>
                </div>
              </div>
            ))}

            {/* 生成中的槽位：常驻展示在已完成段之后，终态后成功的段转正进 videoUrls（位置不变） */}
            {slots.map((slot, i) => {
              const generating = slot.status !== 'completed' && slot.status !== 'failed';
              const failed = slot.status === 'failed';
              return (
                <div
                  key={`slot-${i}`}
                  className="group/item relative min-h-[4.5rem] overflow-hidden rounded-lg border border-dashed border-light-border bg-slate-900"
                >
                  {generating ? (
                    <div className="flex h-full flex-col items-center justify-center gap-1 text-white/70">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-[10px]">生成中</span>
                    </div>
                  ) : failed ? (
                    <div className="flex h-full flex-col items-center justify-center gap-1 px-1 text-center text-rose-300">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span className="line-clamp-2 text-[10px] leading-tight">
                        {slot.errorMessage || '生成失败'}
                      </span>
                    </div>
                  ) : (
                    <video
                      src={resolveBrowserMediaUrl(slot.url as string)}
                      className="h-full w-full object-cover"
                      muted
                      preload="metadata"
                      playsInline
                    />
                  )}
                  <span className="pointer-events-none absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] leading-4 text-white">
                    {urls.length + i + 1}
                  </span>
                  {!generating && (
                    <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition group-hover/item:opacity-100">
                      <SlotButton
                        onClick={() => removeVideoGroupSlot(node.id, i)}
                        title={failed ? '移除失败项' : '移除'}
                      >
                        <Trash2 className="h-3 w-3" />
                      </SlotButton>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* 收起态：横向堆叠前 4 段 */
          <div className="flex items-center gap-1 overflow-hidden">
            {urls.slice(0, 4).map((url, i) => (
              <div
                key={`${url}-${i}`}
                className="h-12 w-20 shrink-0 overflow-hidden rounded border border-light-border bg-black"
              >
                <video
                  src={resolveBrowserMediaUrl(url)}
                  className="h-full w-full object-cover"
                  muted
                  preload="metadata"
                  playsInline
                />
              </div>
            ))}
            {urls.length > 4 && <span className="text-[11px] text-light-muted">+{urls.length - 4}</span>}
          </div>
        )}
      </div>

      {/* 播放预览（portal 到 body，避免被画布 transform 劫持） */}
      {playingIndex !== null &&
        urls[playingIndex] &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
            onClick={() => setPlayingIndex(null)}
          >
            <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setPlayingIndex(null)}
                className="absolute -top-9 right-0 rounded-md p-1 text-white/70 transition hover:text-white"
                title="关闭"
              >
                <X className="h-5 w-5" />
              </button>
              <video
                src={resolveBrowserMediaUrl(urls[playingIndex])}
                className="max-h-[80vh] w-full rounded-lg bg-black"
                controls
                autoPlay
                playsInline
              />
              <p className="mt-2 text-center text-xs text-white/70">
                第 {playingIndex + 1} 段 / 共 {urls.length} 段
              </p>
            </div>
          </div>,
          document.body
        )}

      {/* 视频剪辑弹窗 */}
      {editingClip !== null && urls[editingClip] && (
        <VideoClipEditor
          sourceNode={node}
          clipIndex={editingClip}
          videoUrl={urls[editingClip]}
          durationSec={data.durations?.[editingClip] ?? null}
          onClose={() => setEditingClip(null)}
        />
      )}
    </NodeWrapper>
  );
}
